import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BaseResponse,
  HttpRequestOptions,
  ILoadingOptions,
  ITwoFactorVerificationService,
  LoadingOverlayMode,
  TWO_FACTOR_VERIFICATION_SERVICE,
  isBaseResponse,
  loadingImageHeader,
  loadingOverlayModeHeader,
} from '@shared/models';
import { EMPTY, Observable, OperatorFunction, from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { ModalService } from './modal.service';
import { ToastService } from './toast.service';

const twoFactorVerificationTokenRequestHeaderName = 'X-TwoFactor-Verification-Token';
const requiresVerificationEmailErrorType = 'RequiresVerifiedIdentity';

@Injectable()
export abstract class DataService {
  public baseUrl: string;

  private readonly http = inject(HttpClient);
  protected toastService = inject(ToastService);
  protected modalService = inject(ModalService);
  protected configService = inject(ConfigService);
  public readonly twoFactorVerificationService = inject<ITwoFactorVerificationService>(TWO_FACTOR_VERIFICATION_SERVICE);

  protected constructor(baseUrl: string) {
    this.baseUrl = `${baseUrl}/api`;
  }

  // this function returns an observable of type T in the form of JSON
  // the advantage of an observable : if it changes then this will fire off the changed detection as the DOM has changed
  public get<T>(url: string, loadingOptions: ILoadingOptions | null = null, opt: HttpRequestOptions = {})
    : Observable<T> {
    return this.http.get<T | BaseResponse<T>>(
      `${this.baseUrl}/${url}`, { headers: this.getHeaders(loadingOptions), withCredentials: opt.withCredentials },
    ).pipe(
      this.extractResult(url),
      this.handleUnverifiedIdentity(() => this.get<T>(url, loadingOptions, opt)),
      this.handleError(opt),
    );
  }

  public getForText(url: string, opt: HttpRequestOptions = {}): Observable<string | undefined> {
    return this.http.get<string | BaseResponse<string>>(`${this.baseUrl}/${url}`)
      .pipe(this.extractResult(url), this.handleError(opt));
  }

  public put<T>(
    url: string,
    itemToUpdate: unknown,
    loadingOptions: ILoadingOptions | null = null,
    opt: HttpRequestOptions = {},
  ): Observable<T> {
    return this.http.put<T | BaseResponse<T>>(
      `${this.baseUrl}/${url}`, itemToUpdate, { headers: this.getHeaders(loadingOptions), withCredentials: opt.withCredentials },
    ).pipe(
      this.extractResult(url),
      this.handleUnverifiedIdentity(() => this.put<T>(url, itemToUpdate, loadingOptions, opt)),
      this.handleError(opt),
    );
  }

  public post<T>(
    url: string, body: unknown, loadingOptions: ILoadingOptions | null = null, opt: HttpRequestOptions = {},
  ): Observable<T> {
    return this.http.post<T | BaseResponse<T>>(
      `${this.baseUrl}/${url}`, body, { headers: this.getHeaders(loadingOptions), withCredentials: opt.withCredentials },
    ).pipe(
      this.extractResult(url),
      this.handleUnverifiedIdentity(() => this.post<T>(url, body, loadingOptions, opt)),
      this.handleError(opt),
    );
  }

  public postForText(url: string, body: unknown, opt: HttpRequestOptions = {}): Observable<string | undefined> {
    return this.http.post<string | BaseResponse<string>>(`${this.baseUrl}/${url}`, body)
      .pipe(this.extractResult(url), this.handleError(opt));
  }

  public patch<T>(url: string, body: unknown, opt: HttpRequestOptions = {}): Observable<T> {
    return this.http.patch<T | BaseResponse<T>>(
      `${this.baseUrl}/${url}`, body, { headers: this.getHeaders(null), withCredentials: opt.withCredentials },
    ).pipe(
      this.extractResult(url),
      this.handleUnverifiedIdentity(() => this.patch<T>(url, body, opt)),
      this.handleError(opt),
    );
  }

  public delete<T>(url: string, opt: HttpRequestOptions = {}, body = {}): Observable<T> {
    return this.http.delete<T | BaseResponse<T>>(
      `${this.baseUrl}/${url}`,
      {
        headers: this.getHeaders(null),
        body,
        withCredentials: opt.withCredentials,
      })
      .pipe(
        this.extractResult(url),
        this.handleUnverifiedIdentity(() => this.delete<T>(url, opt)),
        this.handleError(opt),
      );
  }

  public getFile(
    url: string,
    fileType: string | null = null,
    loadingOptions: ILoadingOptions | null = null,
  ): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${url}`, {
        responseType: 'blob',
        observe: 'response',
        headers: this.getHeaders(loadingOptions),
      })
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .pipe(map((res) => new Blob([res.body!], { type: fileType ?? res.headers.get('content-type') ?? undefined })));
  }

  public postFileBlob(url: string, body: unknown, fileType: string | null = null): Observable<Blob> {
    return this.http
      .post(`${this.baseUrl}/${url}`, body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((res) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        new Blob([res.body!], { type: fileType ?? res.headers.get('content-type') ?? undefined }),
      ));
  }

  public postFileArrayBuffer(url: string, body: unknown, opt: HttpRequestOptions = {}): Observable<ArrayBuffer> {
    return this.http
      .post(`${this.baseUrl}/${url}`, body, {
        responseType: 'arraybuffer',
        observe: 'response',
        withCredentials: opt.withCredentials,
      })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        map(res => res.body!),
        this.handleError(opt),
      );
  }

  private getHeaders(loadingOptions: ILoadingOptions | null): HttpHeaders {
    let headers = new HttpHeaders();

    // Loading spinner and image settings
    if (loadingOptions?.loadingImage) {
      headers = headers.set(loadingImageHeader, loadingOptions.loadingImage);
    }
    if (loadingOptions?.loading === false) {
      headers = headers.set(loadingOverlayModeHeader, LoadingOverlayMode.Disable);
    }

    // 'Requires email verification' header
    const identityVerificationToken = this.twoFactorVerificationService.verificationToken;
    if (identityVerificationToken) {
      headers = headers.set(twoFactorVerificationTokenRequestHeaderName, identityVerificationToken);
    }

    return headers;
  }

  /**
   * Extracts the T value from a BaseResponse<T>, throwing an error if the response
   * contained an error.
   */
  private extractResult<T>(url: string): OperatorFunction<T | BaseResponse<T>, T> {
    return map((r: T | BaseResponse<T>) => {
      // TEMPORARILY CHECK TO SEE IF THE RESPONSE IS A BASERESPONSE<T>
      // THIS IS ONLY HERE WHILE THE TRANSITIONS IS MADE TO MAKE CONTROLLERS
      // RETURN A BASERESPONSE.
      if (!isBaseResponse(r)) {
        // eslint-disable-next-line no-console
        console.warn(`[Dev] Request made to '${url}' did not return a BaseResponse.`);
        return r as T;
      }
      if (r.error) {
        throw new Error(r.error.message);
      }
      return r.result as T;
    });
  }

  /**
   * Handles a Forbidden error where the header indicates that the endpoint requires the user to verify their identity.
   * @param onVerified Callback that will be invoked once the user has verified their identity (i.e. to repeat the request)
   */
  private handleUnverifiedIdentity<T>(onVerified: () => Observable<T>): OperatorFunction<T, T> {
    return catchError((error: HttpErrorResponse) =>
      error.status as HttpStatusCode === HttpStatusCode.Forbidden &&
        error.error?.error?.type === requiresVerificationEmailErrorType
        // If the failure is due to the SSO user not having their identity verified, begin the flow to verify it via two-factor auth.
        // If that errors (i.e. failed or cancelled), throw the original (forbidden) error back to dataservice.
        // If verification was successfully, retry the request (which will have the email token in the headers)
        ? from(this.twoFactorVerificationService.beginVerificationFlow())
          .pipe(
            catchError(() => throwError(() => error)),
            switchMap(onVerified),
          )
        : throwError(() => error),
    );
  }

  /**
   * Handles an error in the Http pipeline. Shows a toast notification of the error
    * and rethrows the error message forwards (except for rate-limit errors, which are handled with a modal).
   */
  private handleError<T>(options: HttpRequestOptions): OperatorFunction<T, T> {
    return catchError((err: any) => {
      if (options?.logErrorToConsole ?? true) {
        // eslint-disable-next-line no-console
        console.error('Caught unsuccessful HTTP response: ', err);
      }

      let message = 'An unknown error occurred.';
      let requestId;

      // If the response type is an array buffer, try convert it to JSON
      if (err.error instanceof ArrayBuffer) {
        try {
          err.error = JSON.parse(new TextDecoder().decode(err.error as ArrayBuffer));
        } catch {
          // Not a valid JSON, ignore it
        }
      }

      // Check if err.error is a BaseResponseError
      if (isBaseResponse(err.error)) {
        message = (err.error as BaseResponse<T>).error?.message ?? '';
        requestId = (err.error as BaseResponse<T>).error?.requestId ?? '';
      }

      // Handling other error types, such as 401 Unauthorized.
      if (err instanceof HttpErrorResponse) {
        if (isBaseResponse(err.error)) {
          message = (err.error as BaseResponse<T>).error?.message ?? '';
          requestId = (err.error as BaseResponse<T>).error?.requestId ?? '';
        } else {
          message = err.message;
        }
      }

      if (this.isIgniteRateLimitError(err, message)) {
        const retryAfter = this.extractRetryAfter(message);
        this.modalService.open({
          title: 'Search limit reached',
          message: retryAfter
            ? `You have reached your allowed searches for now. Please try again after ${retryAfter}. Results may not be up to date.`
            : 'You have reached your allowed searches for now. Please try again later. Results may not be up to date.',
          showButtons: true,
          okLabel: 'Ok',
          hideCancel: true,
        }).then(() => null, () => null);

        return EMPTY;
      }

      if ((typeof options?.showErrorToast === 'function' ? options.showErrorToast(err) : options?.showErrorToast) ?? true) {
        const toast = `${message} requestId: ${requestId}`;
        this.toastService.danger(toast, {
          error: true,
          autoHide: false,
          header: 'An error occurred while trying to complete the request',
          message,
          requestId,
        });
      }

      return throwError(() => err);
    });
  }

  private isIgniteRateLimitError(err: unknown, message: string): boolean {
    return (
      (err instanceof HttpErrorResponse && err.status === HttpStatusCode.TooManyRequests && !err.url?.includes('TwoFactorAuthentication'))
      || message.toLowerCase().includes('rate limit exceeded')
    );
  }

  private extractRetryAfter(message: string): string | null {
    const match = /retry after\s*=\s*([^;]+)/i.exec(message);
    return match?.[1]?.trim() || null;
  }
}
